import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { groupTripApi } from '../../api/groupTripApi';
import { groupTripRecruitmentStatusLabel } from './groupTripPresentation';
import {
  activitySchedulePresentation,
  activityStateLabel,
  disruptionStatement,
  formatActualDateTime,
  formatActualTime,
  isIdString,
  isPresentableDisruption,
  originLabel,
  phaseLabel,
  readErrorPresentation,
  timelineEventLabel,
  travelerActualPath,
  travelerStatusLabel,
} from './actualContinuationModel';
import { caseReference } from './disruptionModel';
import './actual-continuation.css';

const list = (value) => (Array.isArray(value) ? value : []);

/** 團體實際行程：只呈現後端唯讀 projection，不讀目前行程重組，也不提供任何修改操作。 */
export default function GroupTripActualPage() {
  const { groupTripId } = useParams();
  const [view, setView] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState(null);
  const generation = useRef(0);

  const load = useCallback(async () => {
    const token = ++generation.current;
    if (!isIdString(groupTripId || '')) {
      setError(readErrorPresentation({ code: 95102 }));
      setPhase('error');
      return;
    }
    setPhase('loading');
    setError(null);
    try {
      const payload = await groupTripApi.getGroupActual(groupTripId);
      if (token !== generation.current) return;
      setView(payload);
      setPhase('ready');
    } catch (cause) {
      if (token !== generation.current) return;
      setError(readErrorPresentation(cause));
      setPhase('error');
    }
  }, [groupTripId]);

  useEffect(() => {
    load();
    return () => { generation.current += 1; };
  }, [load]);

  if (phase === 'loading') {
    return <main className="actual-view actual-view__state" aria-live="polite"><p>載入團體實際行程…</p></main>;
  }
  if (phase === 'error') {
    return (
      <main className="actual-view actual-view__state">
        <p className="actual-view__error" role="alert" data-error-kind={error?.kind}>{error?.message}</p>
        {error?.kind === 'network' || error?.kind === 'unexpected' ? (
          <button type="button" onClick={load}>重新載入</button>
        ) : null}
      </main>
    );
  }

  const activities = list(view?.activities);
  const titleById = new Map(activities.map((item) => [String(item.activityId), item.title]));
  const timeline = list(view?.activityTimeline);
  const participation = view?.participation || {};
  const travelers = list(participation.travelers);
  const disruptions = list(view?.confirmedDisruptions).filter(isPresentableDisruption);
  const viewerTravelers = list(view?.viewerTravelers);

  return (
    <main className="actual-view" data-testid="group-actual">
      <header className="actual-view__header">
        <p className="actual-view__eyebrow">團體實際行程</p>
        <h1>{view?.title}</h1>
        <p className="actual-view__readonly" data-testid="actual-readonly">唯讀 · 依這趟揪團的旅途紀錄整理，無法在此修改</p>
        <nav className="actual-view__nav" aria-label="實際行程導覽">
          {viewerTravelers.map((traveler) => (
            <Link key={traveler.travelerId} to={travelerActualPath(view.groupTripId, traveler.travelerId)}>
              我的實際行程
            </Link>
          ))}
          <Link to={view?.viewerIsHost ? `/group-trips/${view.groupTripId}` : `/public/group-trips/${view.groupTripId}`}>
            返回揪團
          </Link>
        </nav>
      </header>

      <section className="actual-view__panel" aria-labelledby="actual-status-heading">
        <h2 id="actual-status-heading">目前狀況</h2>
        <dl className="actual-view__facts">
          <div><dt>旅途階段</dt><dd data-testid="actual-phase">{view?.phase ? phaseLabel(view.phase) : '尚無法判定'}</dd></div>
          <div><dt>招募狀態</dt><dd>{groupTripRecruitmentStatusLabel(view?.recruitmentStatus)}</dd></div>
          {view?.startAt ? <div><dt>出發</dt><dd>{formatActualDateTime(view.startAt)}</dd></div> : null}
          {view?.endAt ? <div><dt>結束</dt><dd>{formatActualDateTime(view.endAt)}</dd></div> : null}
          <div>
            <dt>出發基準</dt>
            <dd data-testid="actual-baseline">
              {view?.baseline
                ? `已於 ${formatActualDateTime(view.baseline.capturedAt)} 保存出發時的計畫`
                : '尚未保存出發時的計畫，因此沒有可比對的實際行程'}
            </dd>
          </div>
        </dl>
      </section>

      <section className="actual-view__panel" aria-labelledby="actual-activities-heading">
        <h2 id="actual-activities-heading">實際活動</h2>
        {activities.length === 0 ? <p className="actual-view__empty">目前沒有可顯示的活動。</p> : (
          <ol className="actual-view__list" data-testid="actual-activities">
            {activities.map((activity) => {
              const schedule = activitySchedulePresentation(activity);
              return (
                <li key={activity.activityId} className="actual-view__item" data-testid={`actual-activity-${activity.activityId}`}>
                  <p className="actual-view__item-title">
                    <span>{activity.title || '未命名活動'}</span>
                    <span className="actual-view__badge">{originLabel(activity.origin)}</span>
                    <span className={`actual-view__badge${activity.state === 'SCHEDULED' ? '' : ' actual-badge--cancelled'}`}>
                      {activityStateLabel(activity.state)}
                    </span>
                  </p>
                  <p data-schedule-source={schedule.source}>
                    {schedule.source === 'record' ? schedule.text : (
                      <span className={`actual-view__badge actual-badge--${schedule.source === 'unavailable' ? 'unavailable' : 'display-only'}`}>
                        {schedule.text}
                      </span>
                    )}
                  </p>
                  {schedule.note ? <p className="actual-view__secondary">{schedule.note}</p> : null}
                  {activity.state === 'REPLACED' && activity.replacedByActivityId ? (
                    <p className="actual-view__secondary">
                      {`已替換為「${titleById.get(String(activity.replacedByActivityId)) || '另一個活動'}」`}
                    </p>
                  ) : null}
                  {activity.state === 'CANCELLED' && activity.cancellationReason ? (
                    <p className="actual-view__secondary">{`取消原因：${activity.cancellationReason}`}</p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section className="actual-view__panel" aria-labelledby="actual-timeline-heading">
        <h2 id="actual-timeline-heading">旅途變更紀錄</h2>
        {timeline.length === 0 ? <p className="actual-view__empty">旅途中尚未有活動變更紀錄。</p> : (
          <ol className="actual-view__list" data-testid="actual-timeline">
            {timeline.map((entry) => (
              <li key={entry.eventId} className="actual-view__item" data-testid={`actual-event-${entry.eventId}`}>
                <p className="actual-view__item-title">
                  <span>{timelineEventLabel(entry.eventType)}</span>
                  <span>{entry.title || titleById.get(String(entry.activityId)) || ''}</span>
                </p>
                <p className="actual-view__secondary">{formatActualDateTime(entry.occurredAt) || '時間未提供'}</p>
                {entry.eventType === 'ACTIVITY_TIME_CHANGED' ? (
                  <p>{`${timeRange(entry.beforeStartTime, entry.beforeEndTime)} → ${timeRange(entry.afterStartTime, entry.afterEndTime)}`}</p>
                ) : null}
                {entry.eventType === 'ACTIVITY_REPLACED' && entry.replacedActivityId ? (
                  <p>{`取代「${titleById.get(String(entry.replacedActivityId)) || '原活動'}」`}</p>
                ) : null}
                {entry.reason ? <p className="actual-view__secondary">{`原因：${entry.reason}`}</p> : null}
              </li>
            ))}
          </ol>
        )}
      </section>

      <section className="actual-view__panel" aria-labelledby="actual-travelers-heading">
        <h2 id="actual-travelers-heading">目前旅伴狀況</h2>
        <p data-testid="actual-participation-counts">
          {`同行中 ${participation.activeCount ?? 0} 位 · 已離團 ${participation.leftCount ?? 0} 位 · 已移出 ${participation.removedCount ?? 0} 位`}
        </p>
        {travelers.length === 0 ? <p className="actual-view__empty">目前沒有旅伴資料。</p> : (
          <ul className="actual-view__list">
            {travelers.map((traveler) => (
              <li key={traveler.travelerId} className="actual-view__item">
                <p className="actual-view__item-title">
                  <span>{traveler.displayName || '旅伴'}</span>
                  <span className="actual-view__badge">{travelerStatusLabel(traveler.status)}</span>
                </p>
                {traveler.effectiveJoinAt ? (
                  <p className="actual-view__secondary">{`中途加入生效：${formatActualDateTime(traveler.effectiveJoinAt)}`}</p>
                ) : null}
                {view?.viewerIsHost ? (
                  <Link className="actual-view__link" to={travelerActualPath(view.groupTripId, traveler.travelerId)}>
                    查看這位旅伴的實際行程
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="actual-view__panel" aria-labelledby="actual-disruptions-heading">
        <h2 id="actual-disruptions-heading">已確認的行程異動</h2>
        {disruptions.length === 0 ? <p className="actual-view__empty">目前沒有已確認的行程異動。</p> : (
          <ul className="actual-view__list" data-testid="actual-disruptions">
            {disruptions.map((item) => (
              <li key={`${item.caseId}-${item.impactScope}`} className="actual-view__item">
                <p>{disruptionStatement(item)}</p>
                <p className="actual-view__secondary">
                  {[caseReference(item.caseId), item.confirmedAt ? `確認於 ${formatActualDateTime(item.confirmedAt)}` : '']
                    .filter(Boolean).join(' · ')}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function timeRange(start, end) {
  const from = formatActualTime(start);
  const to = formatActualTime(end);
  if (!from && !to) return '未排定時間';
  return to ? `${from || '未提供'}–${to}` : from;
}
